import { type CSSProperties, type FocusEventHandler, useRef, useState, forwardRef, useLayoutEffect } from 'react';
import classnames from 'classnames';

import { keyboardKeys, keyboardMatch, keyboardMatches } from '@hh.ru/magritte-common-keyboard';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { FormHelper } from '@hh.ru/magritte-ui-form-helper';
import { useOTP } from '@hh.ru/magritte-ui-pincode-input/useOTP';

import styles from './pincodeInput.less';

export type PincodeInputSize = 'medium' | 'large';

export interface PincodeInputProps {
    /**
     * Количество цифр в пин-коде, если не указано будет выбрано автоматически
     * в зависимости от размера инпута ( medium - 6, large - 4)
     */
    digitsCount?: number | null;
    /** Размер инпута */
    size?: PincodeInputSize;
    /** Обработчик изменений */
    onChange?: (value: string) => void;
    /** Обработчик фокуса */
    onFocus?: FocusEventHandler<HTMLInputElement>;
    /** Обработчик потери фокуса */
    onBlur?: FocusEventHandler<HTMLInputElement>;
    /** Флаг наличия ошибки */
    invalid?: boolean;
    /** Текст описания */
    description?: string;
    /** Текст сообщения об ошибке */
    errorMessage?: string;
    /**
     * Позволяет перезапустить процесс ожидания OTP sms. Может использоваться например если пользователь перезапросил
     * отправку OTP sms потому что в первый раз отменил чтение кода из sms. Используйте положительные целые числа.
     * */
    credentialsRequestId?: number;
    /**
     * Обработчик фейла запроса OTP Credentials. Он будет вызываться в т.ч. каждый раз при изменении
     * credentialsRequestId т.к. предыдущий запрос будет прерываться.
     * */
    onOTPCredentialsRequestFail?: (error: Error) => void;
    'data-qa'?: string;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const PincodeInput = forwardRef<HTMLDivElement, PincodeInputProps>(
    (
        {
            digitsCount: _digitsCount = null,
            size = 'large',
            invalid = false,
            onFocus,
            onBlur,
            onChange,
            description,
            errorMessage,
            credentialsRequestId = -1,
            onOTPCredentialsRequestFail,
            'data-qa': dataQa = 'magritte-pincode-input',
        },
        ref
    ) => {
        const digitsCount = _digitsCount ?? (size === 'medium' ? 6 : 4);
        const [value, setValue] = useState<string>('');
        const [caretPosition, setCaretPosition] = useState<number>(0);
        const [focused, setFocused] = useState(false);
        const [focusVisible, setFocusVisible] = useState(false);
        const [inputKey, setInputKey] = useState(0);

        const ghostLastDigitRef = useRef<HTMLDivElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        const autofillValueRef = useRef('');

        const updateValue = (newValue: string) => {
            setValue(newValue);
            onChange?.(newValue);
        };

        useOTP(
            (OTPCode) => {
                if (!OTPCode) {
                    return;
                }
                const code = OTPCode.slice(0, digitsCount);
                updateValue(code);
                setCaretPosition(code.length - 1);
                setInputKey((key) => key + 1);
            },
            credentialsRequestId,
            onOTPCredentialsRequestFail
        );

        // внутри этого хука безопасно не мемоизировать рефколлбек, коллбек который будет возвращен будет мемоизирован
        // и не будет меняться при изменении ссылок на аргументы
        const inputRefMultiplexer = useMultipleRefs(inputRef, (element) => {
            if (element && focused) {
                element.focus();
            }
        });

        useLayoutEffect(() => {
            if (!containerRef.current || !ghostLastDigitRef.current) {
                return;
            }

            const lastWidth = ghostLastDigitRef.current.getBoundingClientRect().width;
            containerRef.current.style.setProperty('--magritte-pincode-last-digit-width', `${lastWidth}px`);
        }, [caretPosition, value]);

        const caretPos = clamp(caretPosition, 0, value.length);

        return (
            <div ref={ref} data-qa={dataQa} tabIndex={0} onFocus={() => inputRef.current?.focus()}>
                <div
                    data-qa="magritte-pincode-input-wrapper"
                    ref={containerRef}
                    className={classnames(styles.pincodeInput, {
                        [styles.focus]: focused,
                        [styles.focusVisible]: focusVisible,
                        [styles.medium]: size === 'medium',
                        [styles.invalid]: invalid,
                    })}
                    onMouseDown={(event) => {
                        if (event.target === inputRef.current) {
                            return;
                        }
                        event.preventDefault();

                        const caretPosition = Math.min(
                            Number((event.target as HTMLElement)?.dataset?.digitIndex ?? value.length),
                            value.length
                        );

                        if (!focused) {
                            inputRef.current?.focus();
                        }

                        setCaretPosition(caretPosition);
                    }}
                    style={
                        {
                            '--magritte-pincode-digits-count': digitsCount,
                            '--magritte-pincode-caret-position': caretPos,
                        } as CSSProperties
                    }
                >
                    <input
                        key={inputKey}
                        ref={inputRefMultiplexer}
                        tabIndex={-1}
                        autoComplete="one-time-code"
                        pattern={`\\d{${digitsCount}}`}
                        data-qa="magritte-pincode-input-field"
                        className={styles.digitsInput}
                        type="text"
                        inputMode="numeric"
                        maxLength={digitsCount}
                        // оставляем инпут всегда пустым, т.к. он нам нужен только для показа контекстного меню вставки,
                        // обработки события вставки и обработки фокуса
                        value=""
                        onChange={(event) => {
                            if (/\D/.test(event.target.value)) {
                                return;
                            }

                            // Вставку в инпут и нажатия клавиш цифр мы перехватываем
                            // поэтому если попали сюда, значит использовался autofill (например из sms).
                            // На iOS autofill генерит несколько событий, по количеству заполняемых символов.
                            // Мы не меняем значение самого инпута, поэтому каждое событие будет содержать только
                            // один символ. Сохраняем их и когда их количество достигнет ожидаемого (digitsCount)
                            // подставляем в значение
                            autofillValueRef.current += event.target.value;

                            if (autofillValueRef.current.length >= digitsCount) {
                                updateValue(autofillValueRef.current.slice(0, digitsCount));
                                autofillValueRef.current = '';
                                setCaretPosition(digitsCount - 1);

                                // Кроме того при автозаполнении инпут красится в желтый цвет, что в нашем случае
                                // недопустимо т.к. инпут располагается поверх цифр и должен быть прозрачным. Это нельзя
                                // переопределить с помощью css, есть только хак когда задают transition для цвета фона
                                // это замедлит смену цвета, но в конечном итоге инпут все же поменяет цвет фона.
                                // Поэтому меняем key у инпута, чтобы он перемонтировался и браузер не определял его как
                                // автозаполненный инпут. Важно после перемонтирования не забыть вернуть в него фокус,
                                // иначе для вызова меню вставки потребуется два тапа вместо одного.
                                setInputKey((key) => key + 1);
                            }
                        }}
                        onFocus={(event) => {
                            if (focused) {
                                return;
                            }
                            onFocus?.(event);
                            if (!event.defaultPrevented) {
                                setFocused(true);
                                setFocusVisible(!!inputRef.current?.classList.contains('focus-visible'));
                                if (invalid) {
                                    // По спеке если инпут помечен как invalid при фокусе содержимое должно очищаться
                                    updateValue('');
                                    setCaretPosition(0);
                                }
                            }
                        }}
                        onBlur={(event) => {
                            onBlur?.(event);
                            if (!event.defaultPrevented) {
                                setFocused(false);
                                setFocusVisible(false);
                            }
                        }}
                        onKeyDown={(event) => {
                            if (!inputRef.current) {
                                return;
                            }

                            const isLeft = keyboardMatch(event, keyboardKeys.ArrowLeft);
                            const isRight = keyboardMatch(event, keyboardKeys.ArrowRight);
                            if (isLeft || isRight) {
                                event.preventDefault();
                                setCaretPosition((caretPosition) =>
                                    clamp(caretPosition + (isRight ? 1 : -1), 0, value.length)
                                );
                                return;
                            }

                            if (keyboardMatches(event, keyboardKeys.DigitKeys)) {
                                updateValue(`${value.slice(0, caretPos)}${event.key}${value.slice(caretPos + 1)}`);
                                setCaretPosition((caretPosition) => clamp(caretPosition + 1, 0, digitsCount - 1));
                                event.preventDefault();
                                return;
                            }

                            const isBackspace = keyboardMatch(event, keyboardKeys.Backspace);
                            const isDelete = keyboardMatch(event, keyboardKeys.Delete);

                            if (isBackspace || isDelete) {
                                // При нажатии backspace действует следующая логика:
                                // Если каретка находится в позиции последней введенной цифры, то удаляем её
                                // и оставляем каретку на той же позиции
                                // Если каретка находится в другой позиции, то удаляем цифру слева от каретки и
                                // сдвигаем каретку влево

                                const isLastDigit = caretPos === value.length - 1;
                                const deleteIndex = clamp(
                                    caretPos - (isDelete || isLastDigit ? 0 : 1),
                                    0,
                                    digitsCount - 1
                                );
                                updateValue(`${value.slice(0, deleteIndex)}${value.slice(deleteIndex + 1)}`);
                                setCaretPosition((caretPosition) => {
                                    const newPosition =
                                        isLastDigit || isDelete ? caretPosition : Math.max(caretPosition - 1, 0);

                                    return newPosition;
                                });

                                event.preventDefault();
                            }
                        }}
                        onPaste={(event) => {
                            event.preventDefault();
                            const pasteValue = event.clipboardData.getData('text').replace(/\D/g, '');
                            if (pasteValue.length === digitsCount) {
                                updateValue(pasteValue);
                                setCaretPosition(digitsCount - 1);
                                return;
                            }

                            const newValue =
                                `${value.slice(0, caretPos)}${pasteValue}${value.slice(caretPos + pasteValue.length)}`.slice(
                                    0,
                                    digitsCount
                                );
                            updateValue(newValue);
                            setCaretPosition(Math.min(digitsCount - 1, caretPos + pasteValue.length));
                        }}
                    />
                    <div className={styles.digitBoxContainer}>
                        {/* eslint-disable-next-line no-restricted-properties */}
                        {Array.from({ length: digitsCount }).map((_, index) => (
                            <div key={index} className={classnames(styles.digitBox)} data-digit-index={index}>
                                <span
                                    className={classnames(styles.valueContainer, {
                                        // в позицию ничего не введено, и каретка не находится в этой позиции
                                        [styles.placeholder]:
                                            index >= value.length &&
                                            (!focused ||
                                                (index !== caretPos &&
                                                    !(caretPos === digitsCount && index === digitsCount - 1))),
                                        // в позицию введена цифра, каретка находится в этой позиции, фокус
                                        // установлен в инпут, это не крайняя справа цифра из введенных
                                        [styles.caretLeft]: index === caretPos && index !== value.length - 1 && focused,
                                        // в позицию введена цифра, каретка находится в этой позиции, либо в следующей
                                        // если введены все цифры (потому что при вводе последней цифры каретка
                                        // должна уходить за нее), фокус установлен в инпут, это крайняя справа цифра
                                        // из введенных
                                        [styles.caretRight]:
                                            (index === caretPos ||
                                                (index === digitsCount - 1 && index === caretPos - 1)) &&
                                            index === value.length - 1 &&
                                            focused,
                                    })}
                                    data-qa={`magritte-pincode-input-digit-${index}`}
                                >
                                    {value[index] || (caretPos === index && focused ? '' : '•')}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className={styles.ghostValue} ref={ghostLastDigitRef}>
                        {value.slice(caretPos, caretPos + 1)}
                    </div>
                </div>
                <FormHelper
                    invalid={invalid}
                    description={description}
                    errorMessage={errorMessage}
                    data-qa="magritte-pincode-input-message"
                />
            </div>
        );
    }
);

PincodeInput.displayName = 'PincodeInput';
